import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, X, Send, ArrowRight, ExternalLink, UserRound } from "lucide-react";
import { BACKEND_URL } from "../lib/api";
import { lp } from "../lib/urls";

const getSid = () => {
  let sid = localStorage.getItem("vr_chat_sid");
  if (!sid) {
    sid = `web-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem("vr_chat_sid", sid);
  }
  return sid;
};

const LINK_RE = /\[([^\]]+)\]\((\/[^)\s]*)\)/g;

const parseMessage = (text) => {
  const clean = text.replace(/OPEN:\/[\w\-/]+\s*$/, "").trim();
  const links = [];
  const body = clean.replace(LINK_RE, (_, label, path) => {
    links.push({ label, path });
    return label;
  });
  return { body, links };
};

const TypingDots = () => (
  <span className="inline-flex gap-1 items-center py-1">
    {[0, 1, 2].map((i) => (
      <span key={i} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${i * 140}ms` }} />
    ))}
  </span>
);

export const ChatWidget = () => {
  const { t } = useTranslation();
  const QUICK = [
    { label: t("chat.q1"), hint: t("chat.q1h") },
    { label: t("chat.q2"), hint: t("chat.q2h") },
    { label: t("chat.q3"), hint: t("chat.q3h") },
  ];
  const [open, setOpen] = useState(false);
  const [teaserHidden, setTeaserHidden] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [openedPath, setOpenedPath] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open, busy]);

  useEffect(() => {
    if (openedPath && location.pathname !== openedPath) setOpenedPath(null);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, busy]);

  if (location.pathname.startsWith("/admin")) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((p) => [...p, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSid(), message, lang: (localStorage.getItem("vr_lang") || "en").slice(0, 2) }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const d = JSON.parse(ev.slice(6));
          if (d.delta) {
            setMsgs((p) => {
              const n = [...p];
              n[n.length - 1] = { role: "assistant", content: n[n.length - 1].content + d.delta };
              return n;
            });
          }
          if (d.error) {
            setMsgs((p) => {
              const n = [...p];
              n[n.length - 1] = { role: "assistant", content: "Sorry — something went wrong. Please try again." };
              return n;
            });
          }
        }
      }
    } catch {
      setMsgs((p) => {
        const n = [...p];
        n[n.length - 1] = { role: "assistant", content: "Connection issue — please try again." };
        return n;
      });
    }
    setBusy(false);
  };

  const goTo = (path) => {
    setOpenedPath(path);
    navigate(lp(path));
  };

  const renderRich = (text) => {
    const parts = String(text).split(/\*\*([^*]+)\*\*/g);
    return parts.map((p, i) => (i % 2 === 1 ? <strong key={i} className="font-semibold">{p}</strong> : p.replace(/\*\*/g, "")));
  };

  return (
    <>
      {!open && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
          {!teaserHidden && (
            <div
              data-testid="chat-widget-teaser"
              className="hidden sm:flex items-center gap-2 bg-white border border-gray-200/60 rounded-full pl-4 pr-2 py-2.5 shadow-[0_8px_30px_rgb(0,0,0,0.08)] animate-in fade-in slide-in-from-bottom-2 duration-500"
            >
              <button onClick={() => setOpen(true)} className="font-sans text-[13px] font-medium text-gray-800 text-left max-w-[240px] leading-snug focus-visible:ring-2 focus-visible:ring-[#0a0a0a] outline-none">
                {t("chat.teaser")}
              </button>
              <button onClick={() => setTeaserHidden(true)} aria-label="Dismiss" data-testid="chat-teaser-dismiss" className="p-1 rounded-full text-gray-300 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0">
                <X size={13} />
              </button>
            </div>
          )}
          <button
            onClick={() => setOpen(true)}
            data-testid="chat-widget-open-button"
            aria-label="Chat with VANROCK Assist"
            title="VANROCK Assist"
            className="relative flex items-center justify-center w-12 h-12 rounded-full bg-[#0a0a0a] text-white border border-[#1a1a1a] shadow-[0_10px_30px_rgba(0,0,0,0.3)] hover:bg-gray-800 hover:scale-105 active:scale-95 transition-all focus-visible:ring-2 focus-visible:ring-[#0a0a0a] outline-none"
          >
            <MessageSquare size={20} />
            <span className="absolute top-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-[#0a0a0a] rounded-full" />
          </button>
        </div>
      )}
      {open && (
        <div data-testid="chat-widget-panel" className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 w-full sm:w-[380px] h-[100dvh] sm:h-[620px] sm:max-h-[calc(100vh-100px)] bg-[#fafafa] sm:rounded-2xl overflow-hidden shadow-[0_24px_80px_-12px_rgba(0,0,0,0.25)] flex flex-col animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-300">
          <div className="h-16 bg-[#0a0a0a] border-b border-[#2a2a2a] flex items-center px-4 shrink-0">
            <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-[#1a1a1a] border border-[#2f2f2f] text-yellow-500 shrink-0">
              <UserRound size={17} />
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-[#0a0a0a] rounded-full" />
            </span>
            <div className="flex flex-col gap-1.5 ml-3 flex-1 min-w-0">
              <div className="flex items-center gap-2 leading-none">
                <span className="font-euro-heavy text-[12px] uppercase tracking-[0.08em] text-white leading-none">VANROCK Assist</span>
              </div>
              <div className="font-mono-tech text-[9px] uppercase tracking-[0.15em] text-gray-400 leading-none">
                {t("chat.headerStatus")}
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close-button" aria-label="Close chat" className="text-gray-400 hover:text-white p-1.5 rounded-md hover:bg-[#1a1a1a] transition-colors focus-visible:ring-2 focus-visible:ring-white outline-none">
              <X size={17} />
            </button>
          </div>

          {openedPath && (
            <div className="bg-white/80 backdrop-blur-md border-b border-gray-200/60 py-2 px-4 flex items-center justify-center gap-2 shrink-0" data-testid="chat-opened-banner">
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.1em] text-gray-500 truncate">{t("chat.nowShowing")} {openedPath}</span>
              <ExternalLink size={11} className="text-gray-400 shrink-0" />
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-5" data-testid="chat-messages">
            {msgs.length === 0 && (
              <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="max-w-[92%] self-start bg-white border border-gray-200/60 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                  <p className="font-sans text-[14px] font-semibold text-gray-900 mb-0.5">{t("chat.hi")}</p>
                  <p className="font-sans text-[14px] text-gray-800 leading-relaxed">{t("chat.hiSub")}</p>
                </div>
                <div className="flex flex-col gap-2 mt-1">
                  {QUICK.map((q) => (
                    <button
                      key={q.label}
                      onClick={() => send(q.label)}
                      data-testid={`chat-quick-${q.label.slice(0, 10).replace(/\s/g, "-").toLowerCase()}`}
                      className="group bg-white border border-gray-200/60 rounded-xl p-3 flex items-center justify-between gap-2 cursor-pointer hover:border-[#0a0a0a] shadow-sm hover:shadow-md transition-all text-left focus-visible:ring-2 focus-visible:ring-[#0a0a0a] outline-none"
                    >
                      <span className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-sans text-[13px] font-medium text-gray-900 group-hover:text-yellow-600 transition-colors">{q.label}</span>
                        <span className="font-sans text-[11px] text-gray-500">{q.hint}</span>
                      </span>
                      <ArrowRight size={14} className="text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 shrink-0 transition-all" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => {
              if (m.role === "user") {
                return (
                  <div key={i} className="max-w-[85%] self-end animate-in fade-in slide-in-from-bottom-1 duration-200">
                    <div className="bg-[#0a0a0a] text-white font-sans text-[14px] leading-relaxed px-4 py-2.5 rounded-2xl rounded-tr-sm shadow-md whitespace-pre-wrap">{m.content}</div>
                  </div>
                );
              }
              const { body, links } = parseMessage(m.content);
              return (
                <div key={i} className="max-w-[85%] self-start flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-1 duration-200">
                  {(body || !(busy && i === msgs.length - 1)) ? (
                    <div className="bg-white border border-gray-200/60 font-sans text-[14px] text-gray-800 leading-relaxed px-4 py-2.5 rounded-2xl rounded-tl-sm shadow-sm whitespace-pre-wrap">
                      {renderRich(body)}
                    </div>
                  ) : (
                    <div className="bg-white border border-gray-200/60 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center self-start shadow-sm"><TypingDots /></div>
                  )}
                  {links.length > 0 && (
                    <div className="flex flex-col gap-2 w-full" data-testid="chat-suggestion-cards">
                      {links.slice(0, 5).map((l, j) => (
                        <button
                          key={j}
                          onClick={() => goTo(l.path)}
                          data-testid={`chat-card-${j}`}
                          className="group bg-white border border-gray-200/60 rounded-lg p-3 flex items-center justify-between gap-2 hover:border-[#0a0a0a] shadow-sm transition-colors text-left cursor-pointer focus-visible:ring-2 focus-visible:ring-[#0a0a0a] outline-none"
                        >
                          <span className="min-w-0">
                            <span className="block font-sans text-[13px] font-medium text-gray-900 group-hover:text-yellow-600 truncate transition-colors">{l.label}</span>
                            <span className="block font-mono-tech text-[10px] uppercase tracking-[0.1em] text-gray-500 truncate mt-0.5">{l.path}</span>
                          </span>
                          <ArrowRight size={13} className="text-gray-300 group-hover:text-gray-900 group-hover:translate-x-0.5 shrink-0 transition-all" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          <div className="p-3 bg-white border-t border-gray-200/60 shrink-0">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="bg-[#f4f4f5] rounded-full flex items-center p-1 pl-4 focus-within:bg-white focus-within:ring-1 focus-within:ring-gray-300 transition-all">
              <input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat.placeholder")}
                data-testid="chat-input"
                className="flex-1 bg-transparent font-sans text-[14px] text-gray-900 outline-none placeholder:text-gray-400 min-w-0"
              />
              <button type="submit" disabled={busy} data-testid="chat-send-button" aria-label="Send message" className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0a0a0a] text-white hover:bg-yellow-500 hover:text-[#0a0a0a] transition-colors disabled:opacity-40 shrink-0 focus-visible:ring-2 focus-visible:ring-[#0a0a0a] outline-none">
                <Send size={14} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
